import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';

// Use unified date utils
import { moment, formatServerDateTime } from '../../../utils/dateUtils';

import {
  Box,
  Table,
  TablePagination,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  List,
  ListItem,
  ListItemText,
  Typography,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';

// Icons
import CancelIcon from '@mui/icons-material/Cancel';
import CircleIcon from '@mui/icons-material/Circle';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import BuildIcon from '@mui/icons-material/Build';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Hooks
import usePagination from '../../../hooks/usePagination';

// Styles
import { table_container_base } from '../../../static/styles';

const link_styles = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
};

// Status config
const statusConfig = {
  'Active': { color: '#4caf50', bgColor: '#e8f5e9', icon: CircleIcon, label: 'Active' },
  'In Progress': { color: '#ff9800', bgColor: '#fff3e0', icon: CircleIcon, label: 'In Progress' },
  'Completed': { color: '#2196f3', bgColor: '#e3f2fd', icon: CheckCircleIcon, label: 'Completed' },
  'Cancelled': { color: '#f44336', bgColor: '#ffebee', icon: CancelIcon, label: 'Cancelled' },
};

// Mobile Appointment Card
const MobileAppointmentCard = ({ appointment }) => {
  const navigate = useNavigate();
  const status = statusConfig[appointment.status] || statusConfig['Active'];
  const StatusIcon = status.icon;

  return (
    <Box
      onClick={() => navigate(`/appointments/${appointment.id}`)}
      sx={{
        p: 2,
        borderBottom: '1px solid #eee',
        cursor: 'pointer',
        '&:active': { backgroundColor: '#f5f5f5' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <CalendarTodayIcon sx={{ fontSize: 16, color: '#666', mr: 0.5 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {appointment.startTime ? moment(appointment.startTime).format('MMM D, YYYY') : 'In Queue'}
          </Typography>
        </Box>
        <Chip
          icon={<StatusIcon sx={{ fontSize: 12, color: status.color + ' !important' }} />}
          label={status.label}
          size="small"
          sx={{
            height: 22,
            backgroundColor: status.bgColor,
            color: status.color,
            fontWeight: 600,
            fontSize: 10,
            '& .MuiChip-icon': { color: status.color },
          }}
        />
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <BuildIcon sx={{ fontSize: 14, color: '#999', mr: 0.5 }} />
        <Typography variant="body2" sx={{ color: '#666' }}>
          {appointment.type?.name || '-'}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ fontSize: 14, color: '#999', mr: 0.5 }} />
          <Typography variant="body2" sx={{ color: '#666' }}>
            {appointment.user?.fullName || 'Unassigned'}
          </Typography>
        </Box>
        <ChevronRightIcon sx={{ color: '#ccc' }} />
      </Box>

      {appointment.notes?.length > 0 && (
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #eee' }}>
          <Typography variant="caption" sx={{ color: '#999' }}>
            {appointment.notes.length} note(s)
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const AppointmentAppointmentsTable = ({ appointments }) => {
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ===================== MOBILE VIEW =====================
  if (isMobile) {
    return (
      <Box>
        {appointments?.length ? (
          <>
            {appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
              <MobileAppointmentCard key={appointment.id} appointment={appointment} />
            ))}
            {appointments.length > 5 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={appointments?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ borderTop: '1px solid #eee' }}
              />
            )}
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#999' }}>
              No appointments history
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // ===================== DESKTOP VIEW (unchanged) =====================
  return (
    <Fragment>
      <TableContainer component={Paper} sx={table_container_base}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width="5%">Status</TableCell>
              <TableCell width="10%">Date</TableCell>
              <TableCell width="10%">Type</TableCell>
              <TableCell width="15%">Technician</TableCell>
              <TableCell width="10%">Source</TableCell>
              <TableCell width="50%">Notes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {appointments?.length ? (
              appointments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((appointment) => (
                <TableRow key={appointment.id}>
                  <TableCell scope="row">
                    <Link href={`/appointments/${appointment.id}`} sx={{ ...link_styles, justifyContent: 'center' }}>
                      {appointment.status === 'Active' && (
                        <CircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} />
                      )}
                      {appointment.status === 'In Progress' && (
                        <CircleIcon sx={{ color: 'orange', mr: 1, width: 15, height: 15 }} />
                      )}
                      {appointment.status === 'Completed' && (
                        <CheckCircleIcon sx={{ color: 'green', mr: 1, width: 15, height: 15 }} />
                      )}
                      {appointment.status === 'Cancelled' && (
                        <CancelIcon sx={{ color: 'red', mr: 1, width: 15, height: 15 }} />
                      )}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link href={`/appointments/${appointment.id}`} color="rgba(0, 0, 0, 0.87)" underline="none" sx={link_styles}>
                      {appointment.startTime ? moment(appointment.startTime).format('MM/DD/YYYY') : 'In Queue'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link href={`/appointments/${appointment.id}`} color="rgba(0, 0, 0, 0.87)" underline="none" sx={link_styles}>
                      {appointment.type?.name || '-'}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link href={`/appointments/${appointment.id}`} color="rgba(0, 0, 0, 0.87)" underline="none" sx={link_styles}>
                      {appointment.user?.fullName || (appointment.user ? `${appointment.user.firstName} ${appointment.user.lastName}` : 'Unassigned')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link href={`/appointments/${appointment.id}`} color="rgba(0, 0, 0, 0.87)" underline="none" sx={link_styles}>
                      {appointment.source?.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Link href={`/appointments/${appointment.id}`} color="rgba(0, 0, 0, 0.87)" underline="none" sx={link_styles}>
                      <List sx={{ p: 0 }}>
                        {appointment.notes?.map((note) => (
                          <ListItem sx={{ pl: 0, pr: 0 }} key={note.id}>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="body2">{note.note}</Typography>
                                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.3)', ml: '5px' }}>
                                    (by {note.createdBy?.fullName || (note.createdBy ? `${note.createdBy.firstName} ${note.createdBy.lastName}` : 'Unknown')} at {formatServerDateTime(note.createdAt, 'MM/DD/YYYY hh:mm A')})
                                  </Typography>
                                </Box>
                              }
                              sx={{ m: 0 }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Link>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                  <strong>No Data</strong>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={appointments?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Items per page"
      />
    </Fragment>
  );
};

export default AppointmentAppointmentsTable;
